import React, { useEffect, useState, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useController } from 'react-hook-form';
import {
  Input,
  InputGroupAddon,
  InputGroup,
  Tooltip,
  Button,
  Menu,
  Text,
  Spacer,
  NumberInput,
} from '@oliasoft-open-source/react-ui-library';
import { GroupOrderType } from 'src/enums/general';
import translations from 'src/internationalisation/translation-map.json';
import { normalizeDecimalInputValue } from 'src/common/utils/decimal-input';
import { withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import './style.css';

const PredefinedOptionsMenuState = {
  Custom: 'custom',
  Predefined: 'predefined',
};

const ForwardedGuiInput = forwardRef((props, ref) => {
  return <NumberInput {...props} inputRef={ref} />;
});
ForwardedGuiInput.displayName = 'ForwardedGuiInput';

const RawUnitInput = ({
  testId,
  name,
  control,
  unit,
  errors,
  error = null,
  warning = null,
  predefinedOptions,
  disabled = false,
  width,
  groupOrder = GroupOrderType.FIRST,
  onChange,
  ...inputProps
}) => {
  const { t } = useTranslation();
  const { field } = useController({
    name,
    control,
  });
  const [predefinedOptionsMenuState, setPredefinedOptionsMenuState] = useState(PredefinedOptionsMenuState.Custom);

  let sectionsPredefinedMenu = [];
  const { onChange: fieldOnChange, ...props } = field;
  const foundPredefinedMenuValue = predefinedOptions && predefinedOptions.find((el) => el.value === props.value);

  useEffect(() => {
    if (predefinedOptions) {
      if (foundPredefinedMenuValue) {
        setPredefinedOptionsMenuState(PredefinedOptionsMenuState.Predefined);
      } else {
        setPredefinedOptionsMenuState(PredefinedOptionsMenuState.Custom);
      }
    }
  }, [props.value, foundPredefinedMenuValue]);

  if (predefinedOptions) {
    sectionsPredefinedMenu = [
      {
        type: 'Option',
        inline: true,
        onClick: () => setPredefinedOptionsMenuState(PredefinedOptionsMenuState.Custom),
        label: 'Custom',
        description: 'Custom',
        selected: predefinedOptionsMenuState === PredefinedOptionsMenuState.Custom,
      },
    ].concat(
      predefinedOptions.map((el) => {
        return {
          type: 'Option',
          inline: true,
          onClick: () => {
            setPredefinedOptionsMenuState(PredefinedOptionsMenuState.Predefined);
            fieldOnChange(el.value);
            if (onChange) {
              onChange(el.value);
            }
          },
          label: (
            <>
              <Text>{el.label}</Text>
              <Spacer width="var(--padding-sm)" height="0" />
            </>
          ),
          description: el.value,
          selected: el.value === props.value,
        };
      }),
    );
  }

  return (
    <InputGroup width={width}>
      {predefinedOptions && (
        <Menu
          colored
          maxHeight={380}
          groupOrder={GroupOrderType.FIRST}
          disabled={disabled}
          menu={{
            trigger: 'Component',
            component: (
              <Tooltip
                text={
                  predefinedOptionsMenuState === PredefinedOptionsMenuState.Predefined
                    ? foundPredefinedMenuValue?.label
                    : PredefinedOptionsMenuState.Custom
                }
              >
                <Button
                  groupOrder={GroupOrderType.FIRST}
                  active={predefinedOptionsMenuState === PredefinedOptionsMenuState.Predefined}
                  icon={predefinedOptionsMenuState === PredefinedOptionsMenuState.Predefined ? 'link' : 'unlink'}
                />
              </Tooltip>
            ),
            sections: sectionsPredefinedMenu,
          }}
        />
      )}
      <div className={predefinedOptionsMenuState === PredefinedOptionsMenuState.Predefined ? 'custom-unit-input' : ''}>
        <ForwardedGuiInput
          testId={testId}
          {...props}
          {...inputProps}
          onChange={(e) => {
            fieldOnChange(normalizeDecimalInputValue(e.target.value));
            if (onChange) {
              onChange(e);
            }
          }}
          error={translations[errors[name]] ? t(errors[name]) : errors[name] || error}
          warning={warning}
          disabled={disabled}
          groupOrder={groupOrder}
        />
      </div>
      <InputGroupAddon>
        <span data-testid={`${testId}-unit`}>{unit}</span>
      </InputGroupAddon>
    </InputGroup>
  );
};

export const UnitInput = withErrorBoundary(RawUnitInput);
